import {Component, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {ContactDetailsApiService, ContactDetailsResponse, ContactDetailsV2ApiService} from '@npck/af-frontend-api';
import {LocalizationService, RouterService, rxSpinner} from '@npck/libs/commons';
import {DialogService} from '@npck/libs/ui-kit';
import {Observable, of} from 'rxjs';
import {CONTACT_DETAILS_ID, INCIDENT_ID} from '../../../../../domain/query-params.keys';
import {MainContainerComponent} from '../../../../../layout/main-container/main-container.component';
import {Path} from '../../../../../path';
import {ContactDetailsForm, createContactDetailsRequestV2} from '../../../create-edit/contact-details-step/contact-details-step.component';
import {
    normalizeBlankStringsToUndefined,
} from '../../../create-edit/transaction-information-step/transcation-information/transaction-information.component';

@Component({
    selector: 'af-contact-create-edit',
    standalone: false,
    templateUrl: './contact-create-edit.component.html',
})
export class ContactCreateEditComponent {
    @ViewChild('mainContainer') mainContainer: MainContainerComponent;
    Path = Path;
    form: FormGroup<ContactDetailsForm>;
    incidentId: string;
    contactDetailsId: string;
    contactDetailsResponse: ContactDetailsResponse;

    constructor(
        private activatedRoute: ActivatedRoute,
        private formBuilder: FormBuilder,
        private contactDetailsApiService: ContactDetailsApiService,
        private contactDetailsV2ApiService: ContactDetailsV2ApiService,
        private localizationService: LocalizationService,
        private dialogService: DialogService,
        private routerService: RouterService,
    ) {
        this.incidentId = this.activatedRoute.snapshot.params[INCIDENT_ID];
        this.contactDetailsId = this.activatedRoute.snapshot.params[CONTACT_DETAILS_ID];
        this.form = this.formBuilder.group({});

        if (this.contactDetailsId) {
            this.contactDetailsApiService.getContactDetailsById(this.incidentId, this.contactDetailsId)
                .pipe(rxSpinner('initial'))
                .subscribe(response => this.contactDetailsResponse = response);
        }
    }

    filter = (path: string): boolean => {
        return path === Path.CONTACT_DETAILS;
    };

    breadcrumbCustomizer = (path: string): string => {
        if (this.incidentId === path) {
            return this.localizationService.getLocalizedKey('i18n_lib_breadcrumb#IncidentId', {[INCIDENT_ID]: this.incidentId});
        }
        if (this.contactDetailsId === path) {
            return this.localizationService.getLocalizedKey(
                'i18n_af_labelContactDetailsId', {[CONTACT_DETAILS_ID]: this.contactDetailsId},
            );
        }
        if (path === Path.CONTACT_DETAILS) {
            return '...';
        }
        return path;
    };

    customNavigateToPath = (path?: string): boolean | void => {
        if (path === Path.CONTACT_DETAILS) {
            this.routerService.navigate(`${Path.INCIDENTS}/${this.incidentId}`, {queryParamsHandling: 'merge'});
            return;
        }
        return true;
    };

    navigateBack = (): Observable<boolean> => {
        if (this.form.dirty) {
            return this.dialogService.openDialog({
                title: 'i18n_af_dialogCreateIncidentWarningBeforeExitTitle',
                subtitle: 'i18n_af_dialogCreateIncidentWarningBeforeExitSubtitle',
                buttonOkText: 'i18n_af_buttonExit',
                buttonCancelText: 'i18n_af_buttonCancel',
                isButtonOkAccent: true,
            }, {disableClose: true});
        } else {
            return of(true);
        }
    };

    save(): void {
        this.form.markAllAsTouched();
        if (this.form.invalid) {
            return;
        }
        if (this.contactDetailsId && this.form.pristine) {
            return;
        }
        let request: Observable<any>;
        if (this.contactDetailsId) {
            request = this.contactDetailsV2ApiService.updateContactDetailsById(
                this.incidentId,
                this.contactDetailsId,
                normalizeBlankStringsToUndefined(createContactDetailsRequestV2(this.form.controls.contacts.controls[0])),
            );
        } else {
            request = this.contactDetailsV2ApiService.createContactDetails(
                this.incidentId,
                this.form.controls.contacts.controls
                    .map(contact => normalizeBlankStringsToUndefined(createContactDetailsRequestV2(contact))),
            );
        }
        this.form.disable();
        request
            .pipe(rxSpinner('saveEdit'))
            .subscribe(() => this.mainContainer.breadcrumbs.navigateBackSkipListener());
    }
}
